import dynamic from 'next/dynamic';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";

const AnimatedSection = dynamic(() => import('../Animations/useAnimations'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface BaderExperienceData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  video?: {
    id: string;
    filename_download: string;
  };
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // video
  "video.id",
  "video.filename_download",
] as const;

interface BaderExperienceProps {
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
  data?: BaderExperienceData;
}

export default function BaderExperience({
  zIndex,
  thresholds,
  transitionTime,
  mobileThresholds,
  helperRotation,
  data
}: BaderExperienceProps) {

  
  const videoUrl = data?.video ? `${process.env.NEXT_PUBLIC_DIRECTUS_URL}/assets/${data.video.id}/${data.video.filename_download}` : "";

  return (
		<ScreenAwareContent
			supportedContent={
        <AnimatedSection
          helperRotation={helperRotation}
          className="h-screen overflow-hidden fixed pt-16 xl:pt-32  top-0 left-0"
          thresholds={[
            thresholds?.[0]?.[0],
            thresholds?.[thresholds.length - 1]?.[1],
          ]}
          mobileThresholds={[
            mobileThresholds?.[0]?.[0],
            mobileThresholds?.[mobileThresholds.length - 1]?.[1],
          ]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          variants={{
            desktop: {
              visible: { x: 0, y: 0, opacity: 1 },
              above: { x: "100vw", y: 0, opacity: 1 },
              below: { x: 0, y: "-100vh", opacity: 1 },
            },
            mobile: {
              visible: { x: 0, y: 0, opacity: 1 },
              above: { x: "-100vw", y: 0, opacity: 1 },
              below: { x: 0, y: "-100vh", opacity: 1 },
            },
          }}
        >
          <div className="h-full overflow-hidden relative w-full bg-white">
            {/* Mobile Background Video with Overlay */}
            <div className="lg:hidden w-full h-full relative overflow-hidden">
              <video
                autoPlay
                muted
                loop
                playsInline
									preload="auto"
                className="object-cover w-full h-full"
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
              <div className="absolute left-0 top-0 w-full h-full inset-0 bg-primary/60" />
            </div>

            {/* Content Container */}
            <div className="lg:relative absolute top-0 left-0 h-full flex items-start lg:items-center">
              <div className="hidden lg:block lg:w-[55%]">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
									preload="auto"
                  className="object-cover w-full h-screen"
                >
                  <source src={videoUrl} type="video/mp4" />
                </video>
              </div>

              <div className="lg:w-[40%] lg:pt-0 flex flex-col justify-center h-full">
                <AnimatedSection
                  section="div"
                  className="gl-container gl-desktop-scale flex flex-col justify-center"
                  mobileThresholds={[
                    mobileThresholds?.[0]?.[0],
                    mobileThresholds?.[mobileThresholds.length - 1]?.[1],
                  ]}
                  variants=
                  {{
                    desktop: {
                      visible: { opacity: 1, x: 0 },
                      above: { opacity: 1, x: 0 },
                      below: { opacity: 1, x: 0 },
                    },
                    mobile: {
                      visible: { opacity: 1, x: 0 },
                      above: { opacity: 1, x: "-100vw" },
                      below: { opacity: 1, x: 0 },
                    },
                  }}
                  >
                  <div className="flex flex-col items-start justify-center">
                    <h2 className="text-[2.375rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white lg:text-primary mb-8">
                      {data?.heading?.title || "The Bader Experience"}
                    </h2>
                    <div 
                      className="text-[1rem] text-white lg:text-primary font-greycliff font-normal leading-normal lg:leading-normal tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal lg:[&>*]:text-[1rem] [&>*]:mb-2"
                      dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                    />
                  </div>
                </AnimatedSection>
              </div>
            </div>
          </div>
        </AnimatedSection>
			}
			fallbackContent={
        <section className="overflow-hidden">
          <div className="h-full overflow-hidden relative w-full bg-primary lg:bg-white">

            {/* Content Container */}
            <div className="xl:relative h-full flex items-center lg:items-center">
              {/* Mobile Background Video with Overlay */}
              <div className="xl:hidden w-full h-full absolute inset-0 overflow-hidden">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
									preload="auto"
                  className="object-cover w-full h-full"
                >
                  <source src={videoUrl} type="video/mp4" />
                </video>
                <div className="absolute left-0 top-0 w-full h-full inset-0 bg-primary/60" />
              </div>
              
              <div className="hidden xl:block xl:w-[55%]">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
									preload="auto"
                  className="object-cover w-full h-screen"
                >
                  <source src={videoUrl} type="video/mp4" />
                </video>
              </div>

              <div className="xl:w-[40%] xl:pt-0 flex flex-col justify-center">
                <div className="gl-container gl-desktop-scale">
                  <h2 className="text-[2.375rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white xl:text-primary mb-8">
                    {data?.heading?.title || "The Bader Experience"}
                  </h2>
                  <div 
                    className="text-[1.25rem] text-white xl:text-primary font-greycliff font-normal leading-normal xl:leading-normal tracking-tight [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal xl:[&>*]:text-[1rem] [&>*]:mb-2"
                    dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
			}
    />
  );
}
